<template>
  <div class="practice-result-page" v-loading="loading">
    <div class="top-nav">
      <span class="back-link" @click="goBack">返回模拟练习</span>
      <i class="el-icon-arrow-right"></i>
    </div>

<div class="score-area">
      <div class="score-circle">
        <svg class="progress-ring" viewBox="0 0 100 100" :style="{ strokeDasharray: ringCircumference, strokeDashoffset: ringOffset }">
          <circle class="ring-track" cx="50" cy="50" r="40" />
          <circle class="ring-progress" cx="50" cy="50" r="40" :stroke="ringColor" />
        </svg>
        <img class="bg-time" :src="bgTime" alt="" />
        <img class="purple-circle" :src="purpleCircle" alt="" />
        <div class="score-text">{{ displayRate + '%' }}</div>
        <div class="score-sub">正确率</div>
      </div>
    </div>

    <div class="title">{{ title }}</div>
    <div class="user">
      <el-avatar :size="32">{{ avatarText }}</el-avatar>
      <span class="name">{{ displayName }}</span>
    </div>

    <div class="stats-card">
      <div class="stat">
        <div class="val">{{ total }}</div>
        <div class="label">题目数</div>
      </div>
      <div class="stat">
        <div class="val">{{ wrong }}</div>
        <div class="label">错题数</div>
      </div>
      <div class="stat">
        <div class="val">{{ unanswered }}</div>
        <div class="label">未答数</div>
      </div>
    </div>

    <div v-if="isExam && detailedResults && detailedResults.length" class="details-card">
      <div class="details-title">错题展示</div>
      <div v-for="(q, i) in detailedResults" :key="q.id || i" class="detail-item">
        <div class="detail-header">
          <span class="detail-index">{{ i + 1 }}.</span>
          <span class="detail-status wrong">错误</span>
        </div>
        <div v-if="q.content" class="detail-question" v-html="q.content"></div>
        <div class="detail-row">
          <span class="k">你的答案：</span>
          <span class="v" v-html="formatAnswer(q.userAnswer)"></span>
        </div>
        <div class="detail-row">
          <span class="k">正确答案：</span>
          <span class="v" v-html="formatAnswer(q.correctAnswer, '-')"></span>
        </div>
        <div v-if="q.analysis" class="detail-row answer-analysis">
          <span class="k">解析：</span>
          <span class="v" v-html="q.analysis"></span>
        </div>
      </div>
    </div>

    <div class="bg bg1"></div>
    <div class="bg bg2"></div>
    <div class="bg bg3"></div>
  </div>
</template>

<script>
import { getPaperAnswerResult } from '@/api/examApi'

export default {
  name: 'PaperAnswerResult',
  data() {
    return {
      loading: false,
      resultData: null
    }
  },
  computed: {
    paperId() {
      return this.$route.query.paperId
    },
    isExam() {
      return this.resultData && this.resultData.paperType === 2 // Assuming 2 is exam, 3 is practice based on context, adjust if needed
    },
    title() {
      return this.resultData ? (this.resultData.paperName || '答题结果') : '答题结果'
    },
    actualScore() {
      return this.resultData && this.resultData.examRecord ? Number(this.resultData.examRecord.totalScore || 0) : 0
    },
    totalScore() {
      // If there's no total score provided in the new structure, we default to 100 or sum of question scores
      return 100
    },
    isPassed() {
      const passScore = this.resultData && this.resultData.examRecord && this.resultData.examRecord.passScore != null ? Number(this.resultData.examRecord.passScore) : 60
      return this.actualScore >= passScore
    },
    displayRate() {
      if (this.total === 0) return 0
      const correctCount = this.resultData && this.resultData.answers ? this.resultData.answers.filter(a => a.isCorrect === 1).length : 0
      return Math.min(100, Math.max(0, Math.round((correctCount / this.total) * 100)))
    },
    total() {
      return this.resultData && this.resultData.answers ? this.resultData.answers.length : 0
    },
    wrong() {
      if (!this.resultData || !this.resultData.answers) return 0
      return this.resultData.answers.filter(a => a.isCorrect === 0).length
    },
    unanswered() {
      if (!this.resultData || !this.resultData.answers) return 0
      return this.resultData.answers.filter(a => !a.studentAnswer).length
    },
    detailedResults() {
      if (!this.resultData || !this.resultData.answers) return []
      return this.resultData.answers.filter(a => a.isCorrect === 0).map(a => ({
        id: a.id,
        isCorrect: false,
        content: a.questionContent,
        userAnswer: a.studentAnswer,
        correctAnswer: a.correctAnswer,
        analysis: a.analysis || ''
      }))
    },
    bgTime() {
      return require('@/assets/image/bg_time.png')
    },
    purpleCircle() {
      return require('@/assets/image/icon_purple_circle.png')
    },
    ringCircumference() {
      const r = 40
      return 2 * Math.PI * r
    },
    ringOffset() {
      return this.ringCircumference * (1 - this.displayRate / 100)
    },
    ringColor() {
      if (this.isExam && !this.isPassed) return '#ff4d4f'
      return '#3E2CCD'
    },
    displayName() {
      if (this.resultData && this.resultData.examRecord && this.resultData.examRecord.studentName) {
        return this.resultData.examRecord.studentName
      }
      const store = this.$store
      const loggedIn = store && store.getters && store.getters['auth/isLoggedIn']
      if (!loggedIn) return '未登录'
      const username = store.getters['auth/username']
      const info = store.getters['auth/userInfo'] || {}
      return username || info.mobile || info.phone || info.nickname || '用户'
    },
    avatarText() {
      const name = this.displayName || ''
      return name ? String(name).trim().slice(0, 1) : 'U'
    }
  },
  created() {
    this.fetchResult()
  },
  methods: {
    async fetchResult() {
      if (!this.paperId) return
      this.loading = true
      try {
        const res = await getPaperAnswerResult(this.paperId)
        if (res.code === 200 || res.code === 0) {
          this.resultData = res.data || {}
        } else {
          this.$message.error(res.message || '获取答题结果失败')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('获取答题结果失败')
      } finally {
        this.loading = false
      }
    },
    formatAnswer(val, emptyText = '未答') {
      if (val == null || val === '') return emptyText
      if (Array.isArray(val)) {
        const s = val.join('、').trim()
        return s ? s : emptyText
      }
      const s = String(val).trim()
      return s ? s : emptyText
    },
    goBack() {
      this.$router.push('/member-center/simulation')
    }
  }
}
</script>

<style lang="scss" scoped>
.practice-result-page {
  position: relative;
  min-height: 100vh;
  background: #fff;
  padding: 40px 20px 60px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.top-nav {
  width: 100%;
  max-width: 900px;
  text-align: right;
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;

  .back-link { cursor: pointer; }
}

.score-area { margin-top: 10px; }

.score-circle {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(124,58,237,0.25);
  
  .progress-ring {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    transform: rotate(90deg) scale(-1, 1);
    
    .ring-track {
      fill: none;
      stroke: #eaeaf2;
      stroke-width: 20;
    }
    .ring-progress {
      fill: none;
      stroke: #722ed1;
      stroke-width: 20;
      stroke-linecap: round;
      transition: stroke-dashoffset 0.4s ease;
    }
  }
  
  .bg-time {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    object-fit: cover;
    width: 200px;
    height: 200px;
    z-index: 2;
  }
  .purple-circle {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    object-fit: cover;
    width: 140px;
    height: 140px;
    z-index: 3;
  }
  
  .score-text {
    font-size: 44px;
    font-weight: 800;
    color: #fff;
    z-index: 4;
  }
  .score-sub {
    position: absolute;
    bottom: 64px;
    font-size: 14px;
    color: #fff;
    z-index: 4;
  }
}

.title {
  margin-top: 18px;
  font-size: 20px;
  font-weight: 600;
  color: #5b21b6;
}

.user {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
}

.stats-card {
  margin-top: 20px;
  display: flex;
  background: #f5f7fa;
  border-radius: 12px;
  overflow: hidden;
}

.stat {
  padding: 18px 40px;
  text-align: center;
}

.stat .val {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}

.stat .label {
  font-size: 12px;
  color: #909399;
}

.details-card {
  margin-top: 18px;
  width: 900px;
  max-width: 100%;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 14px 16px;
  box-sizing: border-box;
}

.details-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.detail-item {
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
}

.detail-item:first-of-type {
  border-top: none;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.detail-index {
  font-weight: 600;
  color: #303133;
}

.detail-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f4f4f5;
  color: #606266;
}

.detail-status.correct {
  background: #f0f9eb;
  color: #67c23a;
}

.detail-status.wrong {
  background: #fef0f0;
  color: #f56c6c;
}

.detail-question {
  color: #303133;
  line-height: 1.6;
  margin-bottom: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
  line-height: 1.8;
}

.detail-row .k { color: #909399; }
.detail-row .v { color: #303133; }

.bg { position: absolute; background: url('@/assets/image/bg_time.png') center/contain no-repeat; opacity: 0.12; }
.bg1 { left: 40px; top: 160px; width: 120px; height: 120px; }
.bg2 { right: 120px; top: 280px; width: 180px; height: 180px; }
.bg3 { right: 60px; top: 100px; width: 60px; height: 60px; }
</style>
